import React, { useEffect, useRef, useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { capitalizeAllFirstLetters, formateDate, formateDateWithTime } from '../../../helpers/commonFunctions'
import { GetOutletManagementList } from '../../../services/outletManagementService'
import QueuePanel, { QueueStatsNotes } from './QueuePanel'
import useQueue, { POLL_MS } from './useQueue'
import { JobNameBadge, KvRow, Section, renderScalar, useCompanies } from './jobDisplay'

// Both EOD queues carry the same job payload — one outlet's business date — and
// differ only in the work they do, so one screen serves both. background-tasks
// holds several job kinds at once, so the row's own name says which task failed;
// each name gets a readable label and what its failure costs.
const MAX_ATTEMPTS = 2

const EOD_TASKS = {
    syncErpReports: {
        label: 'ERP Report Sync',
        impact: "That outlet's day is missing from the ERP — finance will not see the sales until this is retried.",
    },
    'deduct-inventory-stock': {
        label: 'Stock Deduction',
        impact: "The day's recipe ingredients were never deducted — stock reads high and inventory reports overstate on-hand for that outlet until this is retried.",
    },
    'close-order-menu-items': {
        label: 'Item Closing',
        impact: "The menu items of that day's auto-closed orders were never closed — they still sit on a kitchen station, so the KDS and item reports show them as open until this is retried.",
    },
}

// Deleting a failed job throws away the only record that an outlet's day never
// reached the ERP or never had its stock deducted, so both destructive actions
// say so before they run.
const QUEUE_NOTES = {
    retry: ' Re-running is safe: each job checks the database first, so nothing is duplicated.',
    destructive: " A failed job is the only trace that an outlet's day was never processed — removing it loses both the retry and the evidence.",
    backup: ' The file is written on the server (/tmp), not downloaded to this browser.',
}

const COLUMNS = [
    { label: 'S.No', width: '5%' },
    { label: 'Job ID', width: '8%' },
    { label: 'Job Name', width: '13%' },
    { label: 'Attempts', width: '8%' },
    { label: 'Company', width: '14%' },
    { label: 'Outlet', width: '13%' },
    { label: 'Business Date', width: '11%' },
    { label: 'Failed Reason', width: '19%' },
    { label: 'Queued At', width: '10%' },
    { label: 'Details', width: '8%' },
]

const eodTask = (job) => EOD_TASKS[job?.name] || null

// Jobs get two tries; at two, nothing further happens without a manual retry.
const renderAttempts = (job) => {
    const attempts = job?.attemptsMade != null ? job.attemptsMade : job?.attempts
    if (attempts == null) return 'N/A'
    return Number(attempts) >= MAX_ATTEMPTS
        ? <span className="text-danger" title="Both attempts used — this job will not run again on its own">{attempts} of {MAX_ATTEMPTS}</span>
        : <span>{attempts} of {MAX_ATTEMPTS}</span>
}

// Always midnight UTC; formatting it through the local timezone can shift the day.
const formatBusinessDate = (value) => {
    if (!value) return 'N/A'
    return formateDate(`${String(value).slice(0, 10)}T12:00:00`)
}

/**
 * The screen behind the "ERP Sync" and "Stock Deduction" tabs. Both queues run
 * one job per outlet per business date with an identical payload, so they share
 * every column, the job view and the wording; only the alias, the heading and
 * the two explanatory sentences differ.
 *
 * @param queue        alias sent to the API ('erp' | 'deduct-inventory-stock')
 * @param title        heading for the stats card
 * @param depthText    sentence for the "still queued" note
 * @param failedText   sentence for the "failed" note
 */
const EodShapedQueue = ({ queue, title, depthText, failedText }) => {

    const queueState = useQueue(queue, { pollMs: POLL_MS, notes: QUEUE_NOTES })
    const { stats, failedJobs } = queueState

    const companies = useCompanies()

    const [showJobModal, setShowJobModal] = useState(false)
    const [selectedJob, setSelectedJob] = useState(null)

    const closeJobModal = () => {
        setShowJobModal(false)
        setSelectedJob(null)
    }

    // An eod job carries no token: its tenant database names the company instead.
    const jobCompany = (job) => {
        const databaseName = job?.data?.database_name
        if (!databaseName) return null
        return companies.find(item => item?.database_name === databaseName) || null
    }

    const jobCompanyName = (job) => {
        const company = jobCompany(job)
        return company?.name ? capitalizeAllFirstLetters(company.name) : 'N/A'
    }

    // outlet_id is tenant-local, so the outlet list has to be pulled per company
    // that shows up in the failed list. Fetched once per company and cached.
    const [outletsByCompany, setOutletsByCompany] = useState({})
    const requestedOutlets = useRef(new Set())

    useEffect(() => {
        const companyIds = [...new Set(failedJobs.map(job => jobCompany(job)?.id).filter(Boolean))]
        companyIds.forEach(companyId => {
            if (requestedOutlets.current.has(companyId)) return
            requestedOutlets.current.add(companyId)
            const formData = new FormData()
            formData.append("page", 1)
            formData.append("per_page", 500)
            formData.append("sort", JSON.stringify({}))
            formData.append("search", JSON.stringify({}))
            formData.append("global_search", "")
            formData.append("company_id", companyId)
            GetOutletManagementList(formData).then(data => {
                setOutletsByCompany(prev => ({ ...prev, [companyId]: data?.data?.response?.rows || [] }))
            }).catch(error => console.log("outlet list error ====> ", error))
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [failedJobs, companies])

    const jobOutletName = (job) => {
        const outletId = job?.data?.outlet_id
        if (outletId == null) return 'N/A'
        const company = jobCompany(job)
        const outlet = (company ? outletsByCompany[company.id] : null)?.find(row => String(row?.id) === String(outletId))
        return outlet?.outlet_name ? capitalizeAllFirstLetters(outlet.outlet_name) : `Outlet #${outletId}`
    }

    const statsFooter = (
        <QueueStatsNotes
            stats={stats}
            depthText={depthText || 'job(s) still queued or running — normal during the evening EOD window.'}
            failedText={failedText || 'job(s) failed.'}
            completedText="Completed is always zero for this queue by design — a successful job is removed the moment it finishes, so it never means nothing has run today."
        />
    )

    // The panel wraps these in the <tr> and adds the select checkbox.
    const renderRow = (row, i) => (
        <>
            <td>{i + 1}</td>
            <td>{row.id || 'N/A'}</td>
            {/* The job's own name; the readable task label is the tooltip. */}
            <td><JobNameBadge name={row.name} title={eodTask(row)?.label} /></td>
            <td>{renderAttempts(row)}</td>
            <td>{jobCompanyName(row)}</td>
            <td>{jobOutletName(row)}</td>
            <td>{formatBusinessDate(row?.data?.business_date)}</td>
            {/* Verbatim: the reason already names the outlet, the date and which report failed. */}
            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '300px' }}>
                {row.failedReason || 'N/A'}
            </td>
            <td>{row.timestamp ? formateDateWithTime(row.timestamp) : 'N/A'}</td>
            <td>
                <button
                    className="btn ripple btn-main-primary signbtn"
                    title='View job details'
                    onClick={() => { setSelectedJob(row); setShowJobModal(true) }}
                >
                    View
                </button>
            </td>
        </>
    )

    // An eod job's payload is a work order, not a document: database_name is
    // internal and order_ids can run to hundreds, so neither is rendered.
    const renderJob = (job) => {
        const data = job?.data || {}
        const task = eodTask(job)
        const orderCount = Array.isArray(data.order_ids) ? data.order_ids.length : null
        return (
            <>
                <Section title="Job Info">
                    <div className="row">
                        <KvRow label="Job ID" value={renderScalar(job.id)} />
                        <KvRow label="Job Name" value={renderScalar(job.name)} />
                        <KvRow label="Task" value={renderScalar(task?.label || job.name)} />
                        <KvRow label="Company" value={renderScalar(jobCompanyName(job))} />
                        <KvRow label="Outlet" value={renderScalar(jobOutletName(job))} />
                        <KvRow label="Business Date" value={renderScalar(formatBusinessDate(data.business_date))} />
                        <KvRow label="Attempts" value={renderAttempts(job)} />
                        <KvRow label="Queued At" value={job.timestamp ? formateDateWithTime(job.timestamp) : '—'} />
                        {orderCount != null ? <KvRow label="Orders In This Job" value={renderScalar(orderCount)} /> : null}
                    </div>
                </Section>

                {job.failedReason ? (
                    <Section title="Failed Reason">
                        <div className="p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {job.failedReason}
                        </div>
                    </Section>
                ) : null}

                {task?.impact ? (
                    <Section title="What This Means">
                        <p className="mb-2">{task.impact}</p>
                        <p className="mb-0 text-muted small">
                            Retrying is safe — the job checks the database before doing anything, so a re-run cannot duplicate work.
                        </p>
                    </Section>
                ) : null}
            </>
        )
    }

    return (
        <>
            <QueuePanel
                queueState={queueState}
                title={title}
                pollMs={POLL_MS}
                /* Successful jobs are removed on completion, so completed is always 0. */
                showCleanCompleted={false}
                statsFooter={statsFooter}
                columns={COLUMNS}
                renderRow={renderRow}
            />

            <Modal show={showJobModal} onHide={closeJobModal} size="lg" scrollable centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Failed Job
                        {selectedJob?.id ? <small className="text-muted ms-2">#{selectedJob.id}</small> : null}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', background: '#fafbff' }}>
                    {selectedJob ? renderJob(selectedJob) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeJobModal}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default EodShapedQueue
